import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { getCroppedAvatarBlob } from "../lib/cropImage";

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.01;
const ZOOM_WHEEL_STEP = 0.04;

/**
 * Modal cropper for a square / circular avatar.
 *
 * @param {Object} props
 * @param {string} [props.label] Heading, e.g. "Profile picture"
 * @param {boolean} props.open
 * @param {string} props.imageSrc Object or data URL of the selected file
 * @param {() => void} props.onCancel
 * @param {(blob: Blob) => void | Promise<void>} props.onConfirm
 * @returns {JSX.Element}
 */
export default function AvatarCropDialog({
  label = "Profile picture",
  open,
  imageSrc,
  onCancel,
  onConfirm,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const zoomInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(ZOOM_MIN);
    setCroppedAreaPixels(null);
    setBusy(false);
    setError("");
  }, [open, imageSrc]);

  useEffect(() => {
    const input = zoomInputRef.current;
    if (!input) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY > 0 ? -1 : 1;
      setZoom((current) =>
        Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, current + direction * ZOOM_WHEEL_STEP))
      );
    };
    input.addEventListener("wheel", onWheel, { passive: false });
    return () => input.removeEventListener("wheel", onWheel);
  }, [open]);

  const handleCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !busy) {
      onCancel();
    }
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setBusy(true);
    setError("");
    try {
      const blob = await getCroppedAvatarBlob(imageSrc, croppedAreaPixels);
      await onConfirm(blob);
    } catch (err) {
      setError(err?.message || "Could not crop image");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crop {label.toLowerCase()}</DialogTitle>
          <DialogDescription>
            Drag to reposition. Use the slider to zoom so the subject fills the circle.
          </DialogDescription>
        </DialogHeader>

        <div className="avatar-cropper relative mx-6 h-80 overflow-hidden rounded-md bg-ui-muted">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={0}
              aspect={1}
              minZoom={ZOOM_MIN}
              maxZoom={ZOOM_MAX}
              zoomSpeed={0.15}
              cropShape="round"
              showGrid={false}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          ) : null}
        </div>

        <div className="px-6 pt-4">
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="avatar-zoom">
            Zoom
          </label>
          <input
            ref={zoomInputRef}
            id="avatar-zoom"
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={ZOOM_STEP}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="w-full accent-accent"
          />
          {error ? <p className="mt-2 text-sm text-error">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={busy || !croppedAreaPixels}>
            {busy ? "Working…" : "Use photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
